/* eslint-disable max-lines -- will be cleaned up later */
import { G } from '@mobily/ts-belt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import sum from 'lodash/sum';
import { useEffect, useMemo, useState } from 'react';
import { required, useTranslate } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';

import { B2bSavingPlanCategory, isFullyManagedPlan } from '@Common/types/b2b/plans';
import { GetEmployeeLockedFundsResponse } from '@Common/types/bo/b2b/employees/locked_funds';
import { B2bCompanyPlanTypeParameters } from '@Common/types/bo/b2b/plans';
import {
  PostB2bWithdrawalAmountsEndpoint,
  PostB2bWithdrawalEligibleAllocationsResponseBody,
} from '@Common/types/bo/b2b/withdrawals';
import { B2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';

import { ShowSection, TypographyTranslated } from '@sh4res/bo-common/components';
import { AmountInput, formatValue, MaxButton } from '@sh4res/bo-common/fields';
import { useCreatePromise, useResourceBasePath } from '@sh4res/bo-common/hooks';
import {
  ParsedAllocation,
  parseAllocations,
} from '@sh4res/bo-common/resources/b2b/withdrawals/common';
import type { From } from '@sh4res/bo-common/resources/b2b/withdrawals/steps/GeneralInfoStep';
import { maxValueCents, minValueCents } from '@sh4res/bo-common/utils';

const testId = 'BoExternalB2bWithdrawalsCreateAmountsStep';

export const AmountsStep = () => {
  const lockedFunds: GetEmployeeLockedFundsResponse | null = useWatch({
    name: 'locked_funds',
    defaultValue: null,
  });
  const eligibleAllocations: PostB2bWithdrawalEligibleAllocationsResponseBody = useWatch({
    name: 'eligible_allocations',
    defaultValue: null,
  });
  const from: From | null = useWatch({
    name: 'from',
    defaultValue: null,
  });

  if (G.isNullable(lockedFunds) || G.isNullable(from)) {
    return null;
  }

  // For early withdrawals, filter lockedFunds using eligible_allocations
  const fundsToUse =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test-only escape hatch set on `window`
    from === 'early' && !(window as any).__SKIP_ALLOCATION_CHECK__
      ? eligibleAllocations
      : lockedFunds;

  return match(from)
    .with('early', () => <EarlyWithdrawal lockedFunds={fundsToUse} />)
    .with('available', () => <PlanWithdrawal lockedFunds={lockedFunds} />)
    .exhaustive();
};

type InstrumentData = {
  instrument_id: number;
  plan_id: string;
  category: B2bSavingPlanCategory;
  amount: number;
  short_name: string | null;
  symbol: string;
};

type FullyManagedPlanData = {
  plan_id: string;
  type: string;
  category: B2bSavingPlanCategory;
  amount: number;
};

type InstrumentAmountTableProps = {
  category: B2bSavingPlanCategory;
  instruments: InstrumentData[];
  fullyManagedPlans: FullyManagedPlanData[];
  fieldPrefix: From;
};

const InstrumentAmountTable = ({
  category,
  instruments,
  fullyManagedPlans,
  fieldPrefix,
}: InstrumentAmountTableProps) => {
  const translate = useTranslate();
  const [isOpen, setIsOpen] = useState(true);

  if (instruments.length === 0 && fullyManagedPlans.length === 0) {
    return null;
  }
  return (
    <>
      <TableRow sx={{ '& td': { borderBottom: 1, borderColor: 'divider' } }}>
        <TableCell colSpan={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
            <IconButton
              data-testid={`${testId}_ToggleCategoryButton`}
              size="small"
              onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            {translate(`resources.bo_common.b2b.companies.saving_plans.types_short.${category}`)}
          </Box>
        </TableCell>
      </TableRow>

      {fullyManagedPlans.map((plan) => (
        <TableRow key={plan.plan_id} sx={{ '& td': { borderBottom: 1, borderColor: 'divider' } }}>
          <TableCell colSpan={2} sx={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1, pl: 6, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: '500' }}>{plan.type}</Typography>
                </Box>
                <Box
                  sx={{
                    width: '150px',
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                    gridColumnGap: '10px',
                  }}>
                  <AmountInput
                    testId={`${testId}_PlanAmountInput`}
                    source={`allocations.${fieldPrefix}.plan-${plan.plan_id}`}
                    label={false}
                    validate={[required(), minValueCents(0), maxValueCents(plan.amount)]}
                    defaultValue={0}
                    min={0}
                    max={plan.amount / 100}
                  />
                  <Box margin={'auto'}>€</Box>
                  <MaxButton
                    data-testid={`${testId}_PlanMaxButton`}
                    field={`allocations.${fieldPrefix}.plan-${plan.plan_id}`}
                    value={plan.amount}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                    {translate('resources.bo_common.b2b.withdrawals.create.subtitles.available', {
                      available: (plan.amount / 100).toFixed(2),
                    })}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ))}

      {instruments.map((instrument) => (
        <TableRow
          key={`${instrument.instrument_id}-${instrument.plan_id}`}
          sx={{ '& td': { borderBottom: 1, borderColor: 'divider' } }}>
          <TableCell colSpan={2} sx={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1, pl: 6, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  {instrument.short_name ? (
                    <>
                      <Typography sx={{ fontWeight: '500' }}>{instrument.short_name}</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', fontSize: '12px' }}>
                        {instrument.symbol}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontWeight: '500' }}>{instrument.symbol}</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    width: '150px',
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                    gridColumnGap: '10px',
                  }}>
                  <AmountInput
                    testId={`${testId}_InstrumentAmountInput`}
                    source={`allocations.${fieldPrefix}.${instrument.instrument_id}-${instrument.plan_id}`}
                    label={false}
                    validate={[required(), minValueCents(0), maxValueCents(instrument.amount)]}
                    defaultValue={0}
                    min={0}
                    max={instrument.amount / 100}
                  />
                  <Box margin={'auto'}>€</Box>
                  <MaxButton
                    data-testid={`${testId}_InstrumentMaxButton`}
                    field={`allocations.${fieldPrefix}.${instrument.instrument_id}-${instrument.plan_id}`}
                    value={instrument.amount}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '14px' }}>
                    {translate('resources.bo_common.b2b.withdrawals.create.subtitles.available', {
                      available: (instrument.amount / 100).toFixed(2),
                    })}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};

const getPositionsFromPlans = (
  plans: GetEmployeeLockedFundsResponse['plans'],
  from: From,
  category: B2bSavingPlanCategory,
): InstrumentData[] =>
  plans
    .filter((plan) => !isFullyManagedPlan(plan.type) && plan.category === category)
    .flatMap((plan) => {
      const positions = from === 'available' ? plan.withdrawable_positions : plan.locked_positions;
      return positions.map((pos) => ({
        ...pos,
        plan_id: plan.plan_id,
        category: plan.category,
      }));
    });

const getFullyManagedPlansForCategory = (
  plans: GetEmployeeLockedFundsResponse['plans'],
  from: From,
  category: B2bSavingPlanCategory,
): FullyManagedPlanData[] =>
  plans
    .filter((plan) => isFullyManagedPlan(plan.type) && plan.category === category)
    .map((plan) => ({
      plan_id: plan.plan_id,
      type: plan.type,
      category: plan.category,
      amount: from === 'available' ? plan.withdrawable : plan.locked,
    }))
    .filter((plan) => plan.amount > 0);

type AmountsCalculationResult = {
  net_amount: number | null;
  gross_amount: number | null;
  tax_amount: number | null;
  currency: string | null;
};

type AmountsRequestBody = {
  employee_id: number;
  company_id?: number;
  allocations: ParsedAllocation[];
  early_reason?: B2BEarlyWithdrawalReason;
  early_event_date?: string;
};

function createAmountsRequestBody(
  employeeId: number,
  companyId: number | null,
  allocations: ParsedAllocation[],
  options?: {
    earlyReason?: B2BEarlyWithdrawalReason | null;
    dateOfEvent?: string | Date | null;
  },
): AmountsRequestBody {
  const requestBody: AmountsRequestBody = {
    employee_id: employeeId,
    allocations,
  };

  if (G.isNotNullable(companyId)) {
    requestBody.company_id = companyId;
  }

  if (G.isNotNullable(options?.earlyReason)) {
    requestBody.early_reason = options.earlyReason;
  }

  if (G.isNotNullable(options?.dateOfEvent)) {
    const eventDate =
      typeof options.dateOfEvent === 'string' ? new Date(options.dateOfEvent) : options.dateOfEvent;
    requestBody.early_event_date = eventDate.toISOString();
  }

  return requestBody;
}

function useCalculateWithdrawalAmounts(
  allocationsData: Record<`${number}-${string}`, number | null>,
  employeeId: number | null,
  companyId: number | null,
  type: 'locked' | 'withdrawable',
  options?: {
    earlyReason?: B2BEarlyWithdrawalReason | null;
    dateOfEvent?: string | Date | null;
    isInternationalCompany?: boolean;
  },
): AmountsCalculationResult {
  const resourceBasePath = useResourceBasePath();
  const [create] = useCreatePromise<
    AmountsRequestBody,
    PostB2bWithdrawalAmountsEndpoint['Client']['ResponseBody']
  >();

  const [result, setResult] = useState<AmountsCalculationResult>({
    net_amount: null,
    gross_amount: null,
    tax_amount: null,
    currency: null,
  });

  const hasNonZeroAmount = Object.values(allocationsData).some((amount: number | null) => {
    if (G.isNullable(amount)) {
      return false;
    }
    return amount > 0;
  });

  useEffect(() => {
    const calculateNetAmount = async () => {
      if (
        !hasNonZeroAmount ||
        G.isNullable(employeeId) ||
        (type === 'locked' &&
          (G.isNullable(options?.dateOfEvent) ||
            (!options.isInternationalCompany && G.isNullable(options.earlyReason))))
      ) {
        setResult({
          net_amount: null,
          gross_amount: null,
          tax_amount: null,
          currency: null,
        });
        return;
      }

      const allocationsArray = parseAllocations(allocationsData, type);

      if (allocationsArray.length === 0) {
        setResult({
          net_amount: null,
          gross_amount: null,
          tax_amount: null,
          currency: null,
        });
        return;
      }

      try {
        const requestBody = createAmountsRequestBody(employeeId, companyId, allocationsArray, {
          earlyReason: options?.earlyReason,
          dateOfEvent: options?.dateOfEvent,
        });

        const data = await create(`${resourceBasePath}withdrawals/amounts`, {
          data: requestBody,
        });

        setResult({
          net_amount: data.net_amount,
          gross_amount: data.gross_amount,
          tax_amount: data.tax_amount,
          currency: data.currency,
        });
      } catch (error) {
        // eslint-disable-next-line no-console -- for debugging
        console.error('Failed to calculate withdrawal amounts:', error);
        setResult({
          net_amount: null,
          gross_amount: null,
          tax_amount: null,
          currency: null,
        });
      }
    };

    void calculateNetAmount();
  }, [
    hasNonZeroAmount,
    employeeId,
    companyId,
    allocationsData,
    type,
    options?.earlyReason,
    options?.dateOfEvent,
    options?.isInternationalCompany,
    create,
    resourceBasePath,
  ]);

  return result;
}

type WithdrawableFunds = Omit<GetEmployeeLockedFundsResponse, 'suspended' | 'suspended_positions'>;

const EarlyWithdrawal = ({ lockedFunds }: { lockedFunds: WithdrawableFunds }) => {
  const translate = useTranslate();

  const allocations: { early?: Record<`${number}-${string}`, number> } | undefined = useWatch({
    name: 'allocations',
  });
  const employeeId = useWatch({ name: 'b2b_employee_id' });
  const companyId = useWatch({ name: 'b2b_company_id' });
  const earlyReason = useWatch({ name: 'early_reason' });
  const dateOfEvent = useWatch({ name: 'date_of_event' });
  const isInternationalCompany = useWatch({
    name: 'is_international',
    defaultValue: false,
  });

  const allocationsData: Record<`${number}-${string}`, number | null> = useMemo(
    () => allocations?.early || {},
    [allocations],
  );

  const amountsResult = useCalculateWithdrawalAmounts(
    allocationsData,
    employeeId,
    companyId,
    'locked',
    {
      earlyReason,
      dateOfEvent,
      isInternationalCompany,
    },
  );

  const amountToWithdraw = sum(Object.values(allocationsData).map((amount) => amount ?? 0));

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.withdrawals.create.sections.from"
      noCard={true}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <AvailableAmounts from="early" lockedFunds={lockedFunds} />
        {G.isNotNullable(amountsResult.net_amount) && !isInternationalCompany && (
          <Typography sx={{ mb: 1 }}>
            {translate('resources.bo_common.b2b.withdrawals.create.labels.amount_to_withdraw', {
              amount: (amountsResult.net_amount / 100).toFixed(2),
            })}
          </Typography>
        )}
        <TypographyTranslated label="resources.bo_common.b2b.withdrawals.create.labels.amounts_estimations_notice" />
        <br />
        <Card>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ height: '50px' }}>
                <TableCell>
                  {translate('resources.bo_common.b2b.withdrawals.create.labels.amount')}
                </TableCell>
                <TableCell>{formatValue(amountToWithdraw)} €</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lockedFunds.categories
                .filter((category) => category.locked > 0)
                .map((category) => (
                  <InstrumentAmountTable
                    key={category.type}
                    category={category.type}
                    instruments={getPositionsFromPlans(lockedFunds.plans, 'early', category.type)}
                    fullyManagedPlans={getFullyManagedPlansForCategory(
                      lockedFunds.plans,
                      'early',
                      category.type,
                    )}
                    fieldPrefix="early"
                  />
                ))}
            </TableBody>
          </Table>
        </Card>
      </Box>
    </ShowSection>
  );
};

const PlanWithdrawal = ({ lockedFunds }: { lockedFunds: GetEmployeeLockedFundsResponse }) => {
  const translate = useTranslate();

  const allocations: { available?: Record<`${number}-${string}`, number> } | undefined = useWatch({
    name: 'allocations',
  });
  const employeeId = useWatch({ name: 'b2b_employee_id' });
  const companyId = useWatch({ name: 'b2b_company_id' });
  const isInternationalCompany = useWatch({ name: 'is_international' });

  const allocationsData: Record<`${number}-${string}`, number | null> = useMemo(
    () => allocations?.available || {},
    [allocations],
  );

  const amountsResult = useCalculateWithdrawalAmounts(
    allocationsData,
    employeeId,
    companyId,
    'withdrawable',
  );

  const amountToWithdraw = sum(Object.values(allocationsData).map((amount) => amount ?? 0));

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.withdrawals.create.sections.from"
      noCard={true}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <AvailableAmounts from="available" lockedFunds={lockedFunds} />
        {G.isNotNullable(amountsResult.net_amount) && !isInternationalCompany && (
          <Typography sx={{ mb: 1 }}>
            {translate('resources.bo_common.b2b.withdrawals.create.labels.amount_to_withdraw', {
              amount: (amountsResult.net_amount / 100).toFixed(2),
            })}
          </Typography>
        )}
        <TypographyTranslated label="resources.bo_common.b2b.withdrawals.create.labels.amounts_estimations_notice" />
        <br />
        <Card>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ height: '50px' }}>
                <TableCell>
                  {translate('resources.bo_common.b2b.withdrawals.create.labels.amount')}
                </TableCell>
                <TableCell>{formatValue(amountToWithdraw)} €</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lockedFunds.categories
                .filter((category) => category.withdrawable > 0)
                .map((category) => (
                  <InstrumentAmountTable
                    key={category.type}
                    category={category.type}
                    instruments={getPositionsFromPlans(
                      lockedFunds.plans,
                      'available',
                      category.type,
                    )}
                    fullyManagedPlans={getFullyManagedPlansForCategory(
                      lockedFunds.plans,
                      'available',
                      category.type,
                    )}
                    fieldPrefix="available"
                  />
                ))}
            </TableBody>
          </Table>
        </Card>
      </Box>
    </ShowSection>
  );
};

const AvailableAmounts = ({
  from,
  lockedFunds,
}: {
  from: 'early' | 'available';
  lockedFunds: WithdrawableFunds;
}) => {
  const translate = useTranslate();

  return (
    <Box>
      {lockedFunds.categories
        .filter((category) =>
          from === 'available' ? category.withdrawable > 0 : category.locked > 0,
        )
        .map((category) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography>{B2bCompanyPlanTypeParameters[category.type].name}</Typography>
            <Typography
              sx={{
                fontSize: '12px',
                marginTop: '-4px',
                color: 'DarkGray',
              }}>
              {match(from)
                .with('available', () =>
                  translate('resources.bo_common.b2b.withdrawals.create.subtitles.available_out', {
                    available: category.withdrawable / 100,
                    total: category.total / 100,
                  }),
                )
                .with('early', () =>
                  translate(
                    'resources.bo_common.b2b.withdrawals.create.subtitles.available_locked',
                    {
                      available: category.locked / 100,
                    },
                  ),
                )
                .exhaustive()}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};
